<ngx-spinner type="ball-atom"></ngx-spinner>
<div class="component-container">
  <div class="component-header">
    <h2>Check Family Status</h2>
  </div>

  <mat-tab-group>
    <mat-tab label="Filters">
      <div class="filters-form">
        <form [formGroup]="filtersForm">
          <div class="form-fields">
            <mat-form-field appearance="fill" class="form-field">
              <mat-label>Problem Type</mat-label>
              <mat-select formControlName="problemType">
                @for (type of problemTypes; track type.value) {
                  <mat-option [value]="type.value">{{ type.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="form-field">
              <mat-label>Country</mat-label>
              <mat-select formControlName="countryId">
                <mat-option value="">All Countries</mat-option>
                @for (country of countries; track country.id) {
                  <mat-option [value]="country.id">{{ country.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="form-field">
              <mat-label>Measurement</mat-label>
              <mat-select formControlName="year">
                <mat-option value="">All Years</mat-option>
                @for (year of measurements; track year) {
                  <mat-option [value]="year">{{ year }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            @let capEnabled = supportsCapitalization(filtersForm.controls.problemType.value);
            <mat-form-field appearance="fill" class="form-field" [class.disabled-field]="!capEnabled">
              <mat-label>Capitalization</mat-label>
              <mat-select formControlName="capitalization" [disabled]="!capEnabled">
                <mat-option value="">All</mat-option>
                @for (cap of capitalizations; track cap.value) {
                  <mat-option [value]="cap.value">{{ cap.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            @if (supportsReview(filtersForm.controls.problemType.value)) {
              <mat-checkbox formControlName="hideReviewed" class="hide-reviewed-toggle">
                Hide reviewed
              </mat-checkbox>
            }
          </div>

          <div class="actions">
            <div class="actions-left">
              <button
                mat-raised-button
                color="primary"
                type="button"
                [disabled]="filtersForm.invalid"
                (click)="onSearch()">
                <mat-icon>search</mat-icon>
                Search
              </button>
              <button
                mat-raised-button
                color="primary"
                type="button"
                [disabled]="filtersForm.invalid"
                (click)="onExport()">
                <mat-icon>file_download</mat-icon>
                Export CSV
              </button>
            </div>
            <button
              mat-raised-button
              color="primary"
              type="button"
              (click)="onExportAll()">
              <mat-icon>file_download</mat-icon>
              Export All
            </button>
          </div>
        </form>
      </div>
    </mat-tab>
  </mat-tab-group>

  @if (hasSearched) {
    @if (results.length > 0) {
      <table mat-table [dataSource]="results" matSort (matSortChange)="onSortChange($event)" class="results-table">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
          <td mat-cell *matCellDef="let row">
            @if (row.name) {
              <span class="name-cell">
                @if (row.directorId) {
                  <a [routerLink]="['/directors/edit', row.directorId]" target="_blank" rel="noopener" class="director-link-btn" title="Edit director">
                    <mat-icon>open_in_new</mat-icon>
                  </a>
                }
                {{ row.name }}
              </span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="company">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Company</th>
          <td mat-cell *matCellDef="let row">
            @if (row.company) {
              <span class="name-cell">
                @if (row.companyId) {
                  <a [routerLink]="['/companies/edit', row.companyId]" [queryParams]="getCompanyQueryParams(row.year)" target="_blank" rel="noopener" class="company-link-btn" title="Edit company">
                    <mat-icon>open_in_new</mat-icon>
                  </a>
                }
                {{ row.company }}
              </span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="country">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Country</th>
          <td mat-cell *matCellDef="let row">{{ row.country }}</td>
        </ng-container>

        <ng-container matColumnDef="year">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Year</th>
          <td mat-cell *matCellDef="let row">{{ row.year }}</td>
        </ng-container>

        <ng-container matColumnDef="occupation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
          <td mat-cell *matCellDef="let row">{{ row.occupation }}</td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
          <td mat-cell *matCellDef="let row">{{ row.status }}</td>
        </ng-container>

        <ng-container matColumnDef="capitalization">
          <th mat-header-cell *matHeaderCellDef>Capitalization</th>
          <td mat-cell *matCellDef="let row">{{ getCapitalizationLabel(row.capitalization) }}</td>
        </ng-container>

        <ng-container matColumnDef="reviewed">
          <th mat-header-cell *matHeaderCellDef
              matTooltip="Tick a director you have checked and found correct — it applies to every year of that director at that company, until you untick it.">
            Reviewed
          </th>
          <td mat-cell *matCellDef="let row">
            <mat-checkbox
              [checked]="row.reviewed"
              [disabled]="reviewingRows.has(reviewKey(row))"
              (change)="onReviewedChange(row, $event.checked)">
            </mat-checkbox>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
      </table>

      <mat-paginator
        [length]="totalItems"
        [pageSize]="pageSize"
        [pageSizeOptions]="pageSizeOptions"
        [pageIndex]="currentPage - 1"
        (page)="onPageChange($event)">
      </mat-paginator>
    } @else {
      <div class="empty-results">No matching records found</div>
    }
  }
</div>
